// Main nav + Support (no Settings tab — see App routes).
import React from 'react';
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as TradingIcon,
  Token as TokenIcon,
  SettingsBackupRestore as RecoveryIcon,
  Replay as RefundIcon,
  Calculate as CalculatorIcon,
  RocketLaunch as LaunchWizardIcon,
  SupportAgent as SupportIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import BrandLogo from './BrandLogo';

const drawerWidth = 272;

const defaultMenuItems = [
  { id: 'launchWizard', label: 'Launch Wizard', icon: <LaunchWizardIcon /> },
  { id: 'distribution', label: 'Launch Planner', icon: <CalculatorIcon /> },
  { id: 'wallet', label: 'Wallet Management', icon: <WalletIcon /> },
  { id: 'trading', label: 'Bundle Launch', icon: <TradingIcon /> },
  { id: 'lpLocker', label: 'LP Locker', icon: <LockIcon /> },
  { id: 'token', label: 'Token Operations', icon: <TokenIcon /> },
  { id: 'recovery', label: 'Backup', icon: <RecoveryIcon /> },
  { id: 'refund', label: 'Refund', icon: <RefundIcon /> },
];

const Sidebar = ({ selectedSection, onSectionChange, onLogoClick, items }) => {
  const theme = useTheme();
  const purple = theme.palette.primary.main;
  const cyan = theme.palette.secondary.main;
  const menuItems = items || defaultMenuItems;

  const navButtonSx = (selected) => ({
    mx: 0.75,
    borderRadius: 2.5,
    py: 1.15,
    px: 1.25,
    border: '1px solid',
    borderColor: selected ? alpha(purple, 0.45) : 'transparent',
    background: selected
      ? `linear-gradient(125deg, ${alpha(purple, 0.18)} 0%, ${alpha('#6366f1', 0.12)} 48%, ${alpha(cyan, 0.1)} 100%)`
      : alpha('#fff', 0.02),
    backdropFilter: selected ? 'blur(10px)' : 'none',
    boxShadow: selected ? `0 0 24px ${alpha(purple, 0.15)}, inset 0 1px 0 ${alpha('#fff', 0.06)}` : 'none',
    transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      backgroundColor: selected ? undefined : alpha('#fff', 0.055),
      borderColor: selected ? alpha(purple, 0.55) : alpha('#fff', 0.08),
    },
    '&.Mui-selected': {
      '&:hover': {
        background: `linear-gradient(125deg, ${alpha(purple, 0.22)} 0%, ${alpha('#6366f1', 0.14)} 50%, ${alpha(cyan, 0.12)} 100%)`,
      },
    },
  });

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${alpha(purple, 0.22)}`,
          background: `linear-gradient(180deg, ${alpha(purple, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.45)} 28%, rgba(6, 7, 12, 0.94) 55%, rgba(4, 5, 10, 0.98) 100%)`,
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          boxShadow: `inset -1px 0 0 ${alpha('#fff', 0.04)}`,
        },
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1.75 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 1.5,
            border: `1px solid ${alpha(purple, 0.22)}`,
            background: `linear-gradient(145deg, ${alpha(purple, 0.09)} 0%, ${alpha(cyan, 0.05)} 100%)`,
            backdropFilter: 'blur(8px)',
            boxShadow: `0 12px 40px ${alpha('#000', 0.35)}`,
          }}
        >
          <BrandLogo size={38} showText={false} onClick={onLogoClick} />
        </Paper>
      </Box>

      <Box sx={{ px: 2.5, pb: 1 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: 1.35,
            fontWeight: 800,
            fontSize: '0.65rem',
            backgroundImage: `linear-gradient(90deg, ${alpha('#e9e7ff', 0.82)} 0%, ${alpha(purple, 0.75)} 50%, ${alpha(cyan, 0.72)} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Navigation
        </Typography>
      </Box>

      <List sx={{ px: 1.25, flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden', py: 0 }}>
        {menuItems.map((item) => {
          const sel = selectedSection === item.id;
          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.65 }}>
              <ListItemButton selected={sel} onClick={() => onSectionChange(item.id)} sx={navButtonSx(sel)}>
                <ListItemIcon
                  sx={{
                    color: sel ? cyan : alpha('#e9e7ff', 0.5),
                    minWidth: 42,
                    filter: sel ? `drop-shadow(0 0 8px ${alpha(cyan, 0.35)})` : 'none',
                    transition: 'color 0.2s ease, filter 0.2s ease',
                    '& svg': { fontSize: 22 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    sx: {
                      fontWeight: sel ? 780 : 640,
                      fontSize: '0.8375rem',
                      letterSpacing: 0.15,
                      color: sel ? alpha('#f5f3ff', 0.96) : alpha('#e9e7ff', 0.76),
                      lineHeight: 1.35,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ flexGrow: 1, minHeight: 8 }} />

      <Divider sx={{ mx: 2, borderColor: alpha('#fff', 0.06) }} />

      <Box sx={{ px: 2, pt: 1.75, pb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(cyan, 0.22)}`,
            background: `linear-gradient(165deg, ${alpha(purple, 0.07)} 0%, ${alpha(theme.palette.background.paper, 0.55)} 40%, ${alpha(cyan, 0.06)} 100%)`,
            backdropFilter: 'blur(10px)',
            boxShadow: `0 8px 32px ${alpha(purple, 0.08)}`,
          }}
        >
          <Stack spacing={1} sx={{ px: 1.5, pt: 1.35, pb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 780,
                letterSpacing: 0.35,
                textAlign: 'center',
                backgroundImage: `linear-gradient(90deg, ${alpha('#ede9fe', 0.95)} 0%, ${purple} 45%, ${cyan} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              BUNDL3R
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                px: 0.5,
                lineHeight: 1.45,
                color: alpha('#e9e7ff', 0.58),
                fontSize: '0.6875rem',
              }}
            >
              Bundle supply and dominate the launch.
            </Typography>
          </Stack>
          <Box
            sx={{
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 'auto 0 0 0',
                height: '45%',
                background: `linear-gradient(180deg, transparent 0%, ${alpha('#05060a', 0.45)} 100%)`,
                pointerEvents: 'none',
              },
            }}
          >
            <Box
              component="img"
              src="/assets/3.png"
              alt=""
              sx={{
                width: '100%',
                height: 'clamp(118px, 17vh, 168px)',
                objectFit: 'contain',
                display: 'block',
                opacity: 0.95,
                filter: `drop-shadow(0 12px 28px ${alpha(cyan, 0.14)})`,
              }}
            />
          </Box>
        </Paper>

        <List sx={{ mt: 1.35, px: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              component="a"
              href="https://t.me/defibitch"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                ...navButtonSx(false),
                '&:hover': {
                  borderColor: alpha(cyan, 0.35),
                  backgroundColor: alpha(cyan, 0.06),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 42, color: alpha(cyan, 0.82), '& svg': { fontSize: 22 } }}>
                <SupportIcon />
              </ListItemIcon>
              <ListItemText
                primary="Support"
                primaryTypographyProps={{ sx: { fontWeight: 700, fontSize: '0.8375rem', color: alpha('#e9e7ff', 0.88) } }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
